<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { usePhoneMask } from 'dashboard/composables/usePhoneMask';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import KanbanCustomFieldsTab from './KanbanCustomFieldsTab.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import kanbanAPI from 'dashboard/api/kanban';
import { isSystemAttributeKey } from 'dashboard/helper/attributeHelper';
import { emitter } from 'shared/helpers/mitt';

const { t } = useI18n();
const store = useStore();
const { maskPhone } = usePhoneMask();

const contactGetter = useMapGetter('contacts/getContact');

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  conversationData: {
    type: Object,
    default: null,
  },
  loadingConversation: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
  notes: {
    type: Array,
    default: () => [],
  },
  checklistItems: {
    type: Array,
    default: () => [],
  },
  attachments: {
    type: Array,
    default: () => [],
  },
});

// Computed para formatar data
const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

// Computed para obter dados combinados da conversa (prioriza dados do item)
const conversationInfo = computed(() => {
  // Primeiro tenta usar os dados da conversa do item (preferência)
  const conversationData = props.item?.conversation;
  if (conversationData) {
    return {
      id: conversationData.id,
      display_id: conversationData.display_id,
      status: conversationData.status,
      last_activity_at: conversationData.last_activity_at,
      unread_count: conversationData.unread_count,
      label_list: conversationData.label_list || [],
      contact: conversationData.contact,
      inbox: conversationData.inbox,
      custom_attributes: conversationData.custom_attributes,
    };
  }

  // Fallback para a prop conversationData (compatibilidade)
  if (props.conversationData) {
    return props.conversationData;
  }

  return null;
});

const contactId = computed(() => conversationInfo.value?.contact?.id);
const contact = computed(() => contactGetter.value(contactId.value));

// Contato a exibir no header - prioriza conversa, fallback para direto
const displayContact = computed(() => {
  // Primeiro: contato da conversa (via store) - verifica se tem dados reais
  if (contact.value && (contact.value.id || contact.value.name)) {
    return contact.value;
  }
  // Segundo: contato da conversa (inline)
  if (conversationInfo.value?.contact) {
    return conversationInfo.value.contact;
  }
  // Terceiro: contato vinculado diretamente (via item.contact)
  if (props.item?.contact) {
    return props.item.contact;
  }
  return null;
});

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

// Watch para carregar dados do contato quando o contactId muda
watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

// Watch para buscar counts quando o item muda
watch(
  () => props.item?.id,
  (newItemId, oldItemId) => {
    if (newItemId && newItemId !== oldItemId) {
      fetchItemCounts(newItemId);
    }
  },
  { immediate: false }
);

// Computed para obter os valores dos atributos do contato do store
const contactAttributeValues = computed(() => {
  if (!contact.value?.custom_attributes) {
    return [];
  }

  // [FIX 2026-02-11] Filter out system attributes (e.g. waha_*)
  return Object.entries(contact.value.custom_attributes)
    .filter(([key]) => !isSystemAttributeKey(key))
    .map(([key, value]) => ({
      key,
      value,
      displayName: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
    }));
});

// Computed para obter os valores dos atributos da conversa diretamente do objeto
const conversationAttributeValues = computed(() => {
  if (!conversationInfo.value?.custom_attributes) {
    return [];
  }

  // [FIX 2026-02-11] Filter out system attributes (e.g. waha_*)
  return Object.entries(conversationInfo.value.custom_attributes)
    .filter(([key]) => !isSystemAttributeKey(key))
    .map(([key, value]) => ({
      key,
      value,
      displayName: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
    }));
});

// Computed para obter os valores dos custom_attributes do item
const itemCustomAttributeValues = computed(() => {
  if (!props.item?.item_details?.custom_attributes) {
    return [];
  }

  return Object.entries(props.item.item_details.custom_attributes).map(
    ([key, value]) => ({
      key,
      value,
      displayName: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
      isArray: Array.isArray(value),
    })
  );
});

// Estado para controlar os accordions
const isItemInfoOpen = ref(false);
const isConversationOpen = ref(false);
const isCustomFieldsOpen = ref(false);
const isContactOpen = ref(false);

// Estado para controlar estatísticas
const statisticsCollapsed = ref(false);

// Estado para armazenar os counts do backend
const itemCounts = ref({
  notes_count: 0,
  checklist_count: 0,
  attachments_count: 0,
});

// Função para alternar estado das estatísticas
const toggleStatistics = () => {
  statisticsCollapsed.value = !statisticsCollapsed.value;
};

// Função para buscar os counts do item
const fetchItemCounts = async itemId => {
  try {
    const response = await kanbanAPI.getCounts(itemId);
    itemCounts.value = response.data;
  } catch (error) {
    console.error('Erro ao buscar counts do item:', error);
    // Fallback para valores calculados localmente
    itemCounts.value = {
      notes_count: props.notes?.length || 0,
      checklist_count: props.checklistItems?.length || 0,
      attachments_count: props.attachments?.length || 0,
    };
  }
};

// Computeds para estatísticas
const totalChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.length;
});

const completedChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.filter(item => item.completed).length;
});

const calculateStageChanges = activities => {
  if (!activities || !Array.isArray(activities)) return 0;
  return activities.filter(activity => activity.type === 'stage_changed')
    .length;
};

const stageChangesCount = computed(() => {
  return calculateStageChanges(props.item.activities);
});

const itemStatistics = computed(() => {
  const timeInStage = props.item.stage_entered_at
    ? formatDistanceToNow(new Date(props.item.stage_entered_at), {
        locale: ptBR,
        addSuffix: false,
      })
    : t('KANBAN.STATISTICS.NOT_AVAILABLE');

  // Usar os counts do backend, com fallback para valores calculados localmente
  const noteCount = itemCounts.value.notes_count;
  const checklistProgressText = `${completedChecklistItems.value}/${itemCounts.value.checklist_count}`;
  const attachmentCount = itemCounts.value.attachments_count;

  return [
    {
      icon: 'lock',
      label: t('KANBAN.STATISTICS.TIME_IN_STAGE'),
      value: timeInStage,
    },
    {
      icon: 'comment-add',
      label: t('KANBAN.STATISTICS.NOTES'),
      value: noteCount,
    },
    {
      icon: 'checkmark-circle',
      label: t('KANBAN.STATISTICS.CHECKLIST'),
      value: checklistProgressText,
    },
    {
      icon: 'attach',
      label: t('KANBAN.STATISTICS.ATTACHMENTS'),
      value: attachmentCount,
    },
    {
      icon: 'arrow-right',
      label: t('KANBAN.STATISTICS.STAGE_CHANGES'),
      value: stageChangesCount.value,
    },
  ];
});

const emit = defineEmits([
  'navigate-to-conversation',
  'context-menu',
  'stage-click',
  'close',
  'update-item',
  'update:item',
  'item-updated',
]);

// v6.4.84: Item labels logic
const itemLabels = computed(() => props.item?.item_details?.labels || []);
const accountLabels = computed(
  () => store.getters['labels/getLabels'] || []
);
const showLabelDropdown = ref(false);
const isLabelsOpen = ref(false);
const isSavingLabel = ref(false);

const getLabelColor = labelName => {
  if (!labelName) return '#64748B';
  const label = accountLabels.value.find(
    l => l.title?.toLowerCase() === labelName.toLowerCase()
  );
  return label?.color || '#64748B';
};

const toggleItemLabel = async labelTitle => {
  if (isSavingLabel.value || !labelTitle) return;
  isSavingLabel.value = true;

  try {
    const currentLabels = [...itemLabels.value];
    const idx = currentLabels.findIndex(
      l => l.toLowerCase() === labelTitle.toLowerCase()
    );

    if (idx >= 0) {
      currentLabels.splice(idx, 1);
    } else {
      currentLabels.push(labelTitle);
    }

    const updatedItemDetails = {
      ...(props.item?.item_details || {}),
      labels: currentLabels,
    };

    const { data } = await kanbanAPI.updateItem(props.item.id, {
      item_details: updatedItemDetails,
    });

    // Atualizar item local com dados do servidor
    const updatedItem = data?.data || data;
    emit('update-item', updatedItem);
    emit('item-updated');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.ERROR'),
      type: 'error',
    });
  } finally {
    isSavingLabel.value = false;
  }
};

const isLabelSelected = labelTitle => {
  return itemLabels.value.some(
    l => l.toLowerCase() === labelTitle.toLowerCase()
  );
};

// Computed: contato tem ID válido e não há conversa vinculada → exibir botão
const canSendMessage = computed(() => {
  return displayContact.value?.id && !conversationInfo.value;
});

const displayContactId = computed(() => {
  const id = displayContact.value?.id;
  return id ? String(id) : null;
});

// Handler: ao criar conversa via ComposeConversation, vincula automaticamente ao item
const isLinkingConversation = ref(false);
const handleConversationCreated = async conversationData => {
  // DEBUG: Verificar dados recebidos do ComposeConversation
  // eslint-disable-next-line no-console
  console.log('[Kanban] handleConversationCreated chamado:', {
    conversationData,
    display_id: conversationData?.display_id,
    id: conversationData?.id,
    itemId: props.item?.id,
    keys: conversationData ? Object.keys(conversationData) : 'null',
  });

  if (!conversationData?.display_id || !props.item?.id) {
    // eslint-disable-next-line no-console
    console.warn('[Kanban] Auto-link cancelado:', {
      hasDisplayId: !!conversationData?.display_id,
      hasItemId: !!props.item?.id,
    });
    return;
  }

  try {
    isLinkingConversation.value = true;
    await kanbanAPI.updateItem(props.item.id, {
      conversation_display_id: conversationData.display_id,
    });

    emitter.emit('newToastMessage', {
      message: 'Conversa criada e vinculada ao item com sucesso!',
      type: 'success',
    });

    emit('item-updated');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[Kanban] Erro ao vincular conversa:', err);
    emitter.emit('newToastMessage', {
      message: 'Conversa criada, mas houve erro ao vincular ao item.',
      type: 'warning',
    });
  } finally {
    isLinkingConversation.value = false;
  }
};

// Carregar dados do contato quando o componente for montado
onMounted(() => {
  getContactDetails();
  // Buscar counts do item se ele existir
  if (props.item?.id) {
    fetchItemCounts(props.item.id);
  }
});
</script>

<template>
  <div class="w-full">
    <!-- Header do contato - mostra quando há contato via conversa OU contato vinculado diretamente -->
    <div
      v-if="displayContact"
      class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mx-2 mb-4"
    >
      <div class="flex items-center gap-4">
        <!-- Avatar -->
        <div class="relative">
          <img
            v-if="displayContact.thumbnail || displayContact.avatar_url"
            :src="displayContact.thumbnail || displayContact.avatar_url"
            :alt="displayContact.name"
            class="w-12 h-12 rounded-full object-cover"
          />
          <div
            v-else
            class="w-12 h-12 bg-woot-500 rounded-full flex items-center justify-center"
          >
            <fluent-icon icon="person" size="24" class="text-white" />
          </div>

          <!-- [COMENTADO] Indicador de status - desativado por causar confusão ao usuário
          <div class="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white dark:ring-slate-800"
               :class="{
                 'bg-green-500': contact.availability_status === 'online',
                 'bg-yellow-500': contact.availability_status === 'busy' || !contact.availability_status,
                 'bg-slate-400': contact.availability_status === 'offline'
               }">
          </div>
          -->
        </div>

        <!-- Informações do contato -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-3 mb-1">
            <h3
              class="text-base font-semibold text-slate-900 dark:text-slate-100 truncate"
            >
              {{ displayContact.name }}
            </h3>
          </div>

          <!-- v6.3.91: Telefone e email em layout vertical (telefone primeiro, depois email) -->
          <div
            class="flex flex-col gap-1 text-xs text-slate-600 dark:text-slate-400"
          >
            <a
              v-if="displayContact.phone_number"
              :href="`tel:${maskPhone(displayContact.phone_number)}`"
              class="flex items-center gap-1 hover:text-green-600 dark:hover:text-green-400"
            >
              <fluent-icon icon="call" size="12" />
              <span>{{ maskPhone(displayContact.phone_number) }}</span>
            </a>
            <a
              v-if="displayContact.email"
              :href="`mailto:${displayContact.email}`"
              class="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400"
            >
              <fluent-icon icon="mail" size="12" />
              <span class="truncate max-w-32">{{ displayContact.email }}</span>
            </a>
          </div>
        </div>

        <!-- Botão Enviar Mensagem — visível quando tem contato mas NÃO tem conversa -->
        <ComposeConversation
          v-if="canSendMessage"
          :contact-id="displayContactId"
          is-modal
          @conversation-created="handleConversationCreated"
        >
          <template #trigger="{ toggle }">
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors shadow-sm shrink-0"
              :disabled="isLinkingConversation"
              @click="toggle"
            >
              <fluent-icon icon="chat" size="14" />
              <span>Enviar Mensagem</span>
            </button>
          </template>
        </ComposeConversation>
      </div>
    </div>

    <!-- Seção de Estatísticas -->
    <div class="item-statistics bg-white dark:bg-slate-800 rounded-lg p-3">
      <!-- Cabeçalho com botão de colapso -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 text-base font-medium">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-chart-no-axes-column-icon lucide-chart-no-axes-column"
          >
            <path d="M5 21v-6" />
            <path d="M12 21V3" />
            <path d="M19 21V9" />
          </svg>
          <span>{{ t('KANBAN.STATISTICS.TITLE') }}</span>
        </div>
        <button
          type="button"
          @click="toggleStatistics"
          class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <fluent-icon
            :icon="statisticsCollapsed ? 'chevron-down' : 'chevron-up'"
            size="16"
          />
        </button>
      </div>

      <!-- Conteúdo colapsável -->
      <div
        v-show="!statisticsCollapsed"
        class="statistics-content transition-all duration-300 ease-in-out"
      >
        <div class="flex flex-col gap-2">
          <div
            v-for="stat in itemStatistics"
            :key="stat.label"
            class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-md w-full"
          >
            <!-- Lado esquerdo: ícone + título -->
            <div class="flex items-center gap-1.5">
              <!-- Ícone inline para tempo na etapa -->
              <div v-if="stat.icon === 'lock'" class="flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-clock-icon lucide-clock text-slate-500 dark:text-slate-400"
                >
                  <path d="M12 6v6l4 2" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </div>
              <!-- Outros ícones continuam usando fluent-icon -->
              <fluent-icon
                v-else
                :icon="stat.icon"
                size="14"
                class="text-slate-500 dark:text-slate-400"
              />
              <span class="text-xs text-slate-500 dark:text-slate-400">
                {{ stat.label }}
              </span>
            </div>

            <!-- Lado direito: valor -->
            <span
              class="text-sm font-medium text-slate-800 dark:text-slate-200"
            >
              {{ stat.value }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Conteúdo do painel -->
    <div class="pb-8 list-group px-2">
      <div class="flex flex-col gap-3">
        <!-- Seção de Informações do Item -->
        <AccordionItem
          :title="
            t('KANBAN.BASIC_DATA_PANEL.ITEM_INFO') || 'Informações do Item'
          "
          :is-open="isItemInfoOpen"
          compact
          @toggle="isItemInfoOpen = !isItemInfoOpen"
        >
          <div class="p-2">
            <!-- Informações básicas do item -->
            <div class="mb-4">
              <div class="flex items-center justify-between mb-2">
                <h3
                  class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                >
                  {{ item.title || item.name || 'Item' }}
                </h3>
                <span
                  class="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded"
                >
                  {{ currentStageName || 'Sem estágio' }}
                </span>
              </div>
              <p
                v-if="itemDescription"
                class="text-sm text-slate-600 dark:text-slate-400"
              >
                {{ itemDescription }}
              </p>
            </div>

            <!-- Atributos do Contato -->
            <div v-if="contactAttributeValues.length > 0" class="mb-4">
              <h4
                class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2"
              >
                Atributos do Contato
              </h4>
              <div class="space-y-2">
                <div
                  v-for="attr in contactAttributeValues"
                  :key="attr.key"
                  class="py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden"
                >
                  <div class="flex items-center justify-between gap-2 min-w-0">
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300 shrink-0"
                    >
                      {{ attr.displayName }}
                    </span>
                    <!-- Valor curto: exibe inline truncado -->
                    <span
                      v-if="String(attr.value).length <= 40"
                      class="text-sm text-slate-900 dark:text-slate-100 truncate min-w-0"
                      :title="String(attr.value)"
                    >
                      {{ attr.value }}
                    </span>
                  </div>
                  <!-- Valor longo: exibe abaixo com quebra e scroll -->
                  <div
                    v-if="String(attr.value).length > 40"
                    class="mt-1 text-sm text-slate-900 dark:text-slate-100 break-all max-h-16 overflow-y-auto bg-slate-100 dark:bg-slate-700/50 rounded px-2 py-1 font-mono text-xs select-all"
                    :title="String(attr.value)"
                  >
                    {{ attr.value }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Atributos da Conversa -->
            <div v-if="conversationAttributeValues.length > 0" class="mb-4">
              <h4
                class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2"
              >
                Atributos da Conversa
              </h4>
              <div class="space-y-2">
                <div
                  v-for="attr in conversationAttributeValues"
                  :key="attr.key"
                  class="py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden"
                >
                  <div class="flex items-center justify-between gap-2 min-w-0">
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300 shrink-0"
                    >
                      {{ attr.displayName }}
                    </span>
                    <!-- Valor curto: exibe inline truncado -->
                    <span
                      v-if="String(attr.value).length <= 40"
                      class="text-sm text-slate-900 dark:text-slate-100 truncate min-w-0"
                      :title="String(attr.value)"
                    >
                      {{ attr.value }}
                    </span>
                  </div>
                  <!-- Valor longo: exibe abaixo com quebra e scroll -->
                  <div
                    v-if="String(attr.value).length > 40"
                    class="mt-1 text-sm text-slate-900 dark:text-slate-100 break-all max-h-16 overflow-y-auto bg-slate-100 dark:bg-slate-700/50 rounded px-2 py-1 font-mono text-xs select-all"
                    :title="String(attr.value)"
                  >
                    {{ attr.value }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Atributos Customizados do Item -->
            <div v-if="itemCustomAttributeValues.length > 0" class="mb-4">
              <h4
                class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2"
              >
                Atributos do Item
              </h4>
              <div class="space-y-2">
                <div
                  v-for="attr in itemCustomAttributeValues"
                  :key="attr.key"
                  class="py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden"
                >
                  <div class="flex justify-between items-center mb-1">
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {{ attr.displayName }}
                    </span>
                  </div>
                  <!-- Se for array, mostra como lista -->
                  <div v-if="attr.isArray" class="mt-2">
                    <div class="flex flex-col gap-1">
                      <span
                        v-for="item in attr.value"
                        :key="item"
                        class="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full w-fit"
                      >
                        {{ item }}
                      </span>
                    </div>
                  </div>
                  <!-- Se não for array e valor curto, mostra como texto normal -->
                  <div
                    v-else-if="String(attr.value).length <= 40"
                    class="text-sm text-slate-900 dark:text-slate-100"
                  >
                    {{ attr.value }}
                  </div>
                  <!-- Se não for array e valor longo, mostra com scroll -->
                  <div
                    v-else
                    class="text-sm text-slate-900 dark:text-slate-100 break-all max-h-16 overflow-y-auto bg-slate-100 dark:bg-slate-700/50 rounded px-2 py-1 font-mono text-xs select-all"
                    :title="String(attr.value)"
                  >
                    {{ attr.value }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Mensagem quando não há atributos -->
            <div
              v-if="
                contactAttributeValues.length === 0 &&
                conversationAttributeValues.length === 0 &&
                itemCustomAttributeValues.length === 0
              "
              class="text-center py-4"
            >
              <span class="text-sm text-slate-500 dark:text-slate-400">
                {{ t('KANBAN.CUSTOM_FIELDS.NO_ATTRIBUTES_FOUND') }}
              </span>
            </div>
          </div>
        </AccordionItem>

        <!-- v6.4.84: Seção de Etiquetas do Item -->
        <AccordionItem
          :title="'Etiquetas do Item'"
          :is-open="isLabelsOpen"
          compact
          @toggle="isLabelsOpen = !isLabelsOpen"
        >
          <div class="p-2">
            <!-- Labels atuais -->
            <div class="flex flex-wrap gap-1.5 mb-3">
              <span
                v-for="label in itemLabels"
                :key="label"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 cursor-pointer hover:opacity-80 transition-opacity"
                :style="{
                  borderLeft: `3px solid ${getLabelColor(label)}`,
                  border: `1px solid ${getLabelColor(label)}30`,
                  borderLeftWidth: '3px',
                  borderLeftColor: getLabelColor(label),
                }"
                @click="toggleItemLabel(label)"
              >
                {{ label }}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="opacity-50 hover:opacity-100"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </span>
              <span
                v-if="itemLabels.length === 0"
                class="text-xs text-slate-400 dark:text-slate-500 italic"
              >
                Nenhuma etiqueta
              </span>
            </div>

            <!-- Botão para adicionar -->
            <button
              type="button"
              class="flex items-center gap-1.5 text-xs text-woot-500 dark:text-woot-400 hover:text-woot-600 dark:hover:text-woot-300 font-medium transition-colors"
              @click="showLabelDropdown = !showLabelDropdown"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              {{ showLabelDropdown ? 'Fechar' : 'Adicionar etiqueta' }}
            </button>

            <!-- Dropdown de seleção -->
            <div
              v-if="showLabelDropdown"
              class="mt-2 max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-lg"
            >
              <div
                v-for="label in accountLabels"
                :key="label.id"
                class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                :class="{
                  'bg-slate-50 dark:bg-slate-700/50': isLabelSelected(
                    label.title
                  ),
                }"
                @click="toggleItemLabel(label.title)"
              >
                <span
                  class="w-3 h-3 rounded-full shrink-0"
                  :style="{ backgroundColor: label.color || '#64748B' }"
                />
                <span
                  class="text-sm text-slate-700 dark:text-slate-200 flex-1 truncate"
                >
                  {{ label.title }}
                </span>
                <svg
                  v-if="isLabelSelected(label.title)"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-woot-500 shrink-0"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <div
                v-if="accountLabels.length === 0"
                class="px-3 py-4 text-center text-xs text-slate-400 dark:text-slate-500"
              >
                Nenhuma etiqueta disponível na conta
              </div>
            </div>
          </div>
        </AccordionItem>

        <!-- Seção de Conversa Vinculada -->
        <AccordionItem
          v-if="conversationInfo"
          :title="
            t('KANBAN.BASIC_DATA_PANEL.CONVERSATION') || 'Conversa Vinculada'
          "
          :is-open="isConversationOpen"
          compact
          @toggle="isConversationOpen = !isConversationOpen"
        >
          <div class="p-2">
            <div
              class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200/30 dark:border-slate-700/30 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              @click="
                $emit(
                  'navigate-to-conversation',
                  $event,
                  conversationInfo.display_id
                )
              "
              @contextmenu="$emit('context-menu', $event, conversationInfo.id)"
            >
              <!-- Contato -->
              <div
                v-if="conversationInfo.contact"
                class="flex items-center gap-3 mb-2"
              >
                <img
                  v-if="conversationInfo.contact.thumbnail"
                  :src="conversationInfo.contact.thumbnail"
                  :alt="conversationInfo.contact.name"
                  class="w-8 h-8 rounded-full object-cover"
                />
                <div
                  v-else
                  class="w-8 h-8 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center"
                >
                  <span
                    class="text-xs font-medium text-slate-600 dark:text-slate-400"
                  >
                    {{
                      conversationInfo.contact.name?.charAt(0)?.toUpperCase() ||
                      '?'
                    }}
                  </span>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <div
                      class="font-semibold text-sm text-slate-900 dark:text-slate-100"
                    >
                      <span
                        class="text-xs text-slate-500 dark:text-slate-400 mr-1"
                        >#{{ conversationInfo.display_id }}</span
                      >
                      {{
                        conversationInfo.contact.name ||
                        t('KANBAN.CONTACT_UNKNOWN')
                      }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  <!-- Status -->
                  <span
                    class="text-xs px-2 py-0.5 rounded-full font-medium"
                    :class="{
                      'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300':
                        conversationInfo.status === 'open',
                      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300':
                        conversationInfo.status === 'pending',
                      'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300':
                        conversationInfo.status === 'resolved',
                    }"
                  >
                    {{ conversationInfo.status }}
                  </span>
                  <!-- Unread count -->
                  <span
                    v-if="conversationInfo.unread_count > 0"
                    class="flex items-center justify-center h-5 min-w-[1rem] px-1.5 text-xs font-medium bg-red-500 text-white rounded-full shadow-sm"
                  >
                    {{
                      conversationInfo.unread_count > 9
                        ? '9+'
                        : conversationInfo.unread_count
                    }}
                  </span>
                </div>
              </div>

              <!-- Labels -->
              <div
                v-if="
                  conversationInfo.label_list &&
                  conversationInfo.label_list.length > 0
                "
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="label in conversationInfo.label_list"
                  :key="label"
                  class="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
                >
                  {{ label }}
                </span>
              </div>
            </div>
          </div>
        </AccordionItem>

        <!-- Seção de Contato Vinculado (quando não há conversa) -->
        <AccordionItem
          v-if="item.contact && !conversationInfo"
          :title="t('KANBAN.BASIC_DATA_PANEL.CONTACT') || 'Contato Vinculado'"
          :is-open="isContactOpen"
          compact
          @toggle="isContactOpen = !isContactOpen"
        >
          <div class="p-2">
            <div
              class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200/30 dark:border-slate-700/30 p-3"
            >
              <!-- Contato vinculado -->
              <div class="flex items-center gap-3 mb-3">
                <img
                  v-if="item.contact.thumbnail || item.contact.avatar_url"
                  :src="item.contact.thumbnail || item.contact.avatar_url"
                  :alt="item.contact.name"
                  class="w-10 h-10 rounded-full object-cover"
                />
                <div
                  v-else
                  class="w-10 h-10 bg-woot-500 rounded-full flex items-center justify-center"
                >
                  <span class="text-sm font-medium text-white">
                    {{ (item.contact.name || 'C').charAt(0).toUpperCase() }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <h4
                    class="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate"
                  >
                    {{ item.contact.name || 'Sem nome' }}
                  </h4>
                  <!-- v6.3.91: Telefone e email em layout vertical (telefone primeiro, depois email) -->
                  <div
                    class="flex flex-col gap-1 text-xs text-slate-600 dark:text-slate-400 mt-0.5"
                  >
                    <a
                      v-if="item.contact.phone_number"
                      :href="`tel:${item.contact.phone_number}`"
                      class="flex items-center gap-1 hover:text-green-600 dark:hover:text-green-400"
                    >
                      <fluent-icon icon="call" size="12" />
                      <span>{{ maskPhone(item.contact.phone_number) }}</span>
                    </a>
                    <a
                      v-if="item.contact.email"
                      :href="`mailto:${item.contact.email}`"
                      class="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      <fluent-icon icon="mail" size="12" />
                      <span class="truncate max-w-24">{{
                        item.contact.email
                      }}</span>
                    </a>
                  </div>
                </div>
              </div>

              <!-- Labels do contato -->
              <div
                v-if="item.contact.labels && item.contact.labels.length > 0"
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="label in item.contact.labels"
                  :key="label"
                  class="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
                >
                  {{ label }}
                </span>
              </div>
            </div>
          </div>
        </AccordionItem>

        <!-- Seção de Dados Adicionais -->
        <AccordionItem
          :title="t('KANBAN.CUSTOM_FIELDS.TITLE') || 'Dados Adicionais'"
          :is-open="isCustomFieldsOpen"
          compact
          @toggle="isCustomFieldsOpen = !isCustomFieldsOpen"
        >
          <div class="p-2">
            <KanbanCustomFieldsTab
              :item="item"
              :is-stacklab="isStacklab"
              @update:item="$emit('update:item', $event)"
              @item-updated="$emit('item-updated')"
            />
          </div>
        </AccordionItem>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-n-weak;
  }
}

/* Estilos para animação de colapso das estatísticas */
.item-statistics {
  /* Estilos já aplicados via classes Tailwind */
}

.statistics-content {
  overflow: hidden;
  transition: all 0.3s ease-in-out;
}

.statistics-content.collapsed {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.statistics-content.expanded {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}
</style>
